/* ============================================================
   SINGSPACE ADMIN — CANDIDATURES (APPLICATIONS) API INTEGRATED
   ============================================================ */

import { CheckCircle2, ChevronLeft, ChevronRight, Clock, Users, XCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ApplicationsFilters } from '../../components/applications/ApplicationsFilters';
import { ApplicationsTable } from '../../components/applications/ApplicationsTable';
import { KPIWidget } from '../../components/common/KPIWidget';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { useApplicationsAdmin } from '../../hooks/useApplicationsAdmin';
import { cn } from '../../lib/utils';

export function ApplicationsPage() {
  const navigate = useNavigate();
  const {
    applications,
    loading,
    error,
    pagination,
    stats,
    statusFilter,
    searchQuery,
    updateFilter,
    updateSearch,
    resetFilters,
    setPage,
  } = useApplicationsAdmin();

  const handleViewApplication = (id: string) => {
    console.log('[ApplicationsPage] Navigating to application review:', id);
    navigate(`/applications/${id}`);
  };

  if (loading) {
    return (
      <div className="space-y-6 pb-12">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Candidatures</h1>
            <p className="text-slate-500 mt-1">Gérez les demandes de réservation nécessitant validation</p>
          </div>
        </div>
        <LoadingSpinner message="Chargement des candidatures..." submessage="Veuillez patienter" size="md" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6 pb-12">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Candidatures</h1>
            <p className="text-slate-500 mt-1">Gérez les demandes de réservation nécessitant validation</p>
          </div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-600 font-medium mb-2">Erreur lors du chargement</p>
          <p className="text-red-500 text-sm">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-12">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Candidatures</h1>
          <p className="text-slate-500 mt-1">Gérez les demandes de réservation nécessitant validation</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <KPIWidget
          title="Total Candidatures"
          value={stats.total.toString()}
          subtext="Historique"
          trend="up"
          color="primary"
          icon={<Users className="w-5 h-5" />}
        />
        <KPIWidget
          title="Nouvelles / À l'étude"
          value={(stats.new + stats.reviewing).toString()}
          subtext="Action requise"
          trend="up"
          color="warning"
          icon={<Clock className="w-5 h-5" />}
        />
        <KPIWidget
          title="Approuvées"
          value={stats.approved.toString()}
          subtext="Prêtes à réserver"
          trend="up"
          color="success"
          icon={<CheckCircle2 className="w-5 h-5" />}
        />
        <KPIWidget
          title="Refusées"
          value={stats.rejected.toString()}
          subtext="Processus clos"
          trend="down"
          color="error"
          icon={<XCircle className="w-5 h-5" />}
        />
        <KPIWidget
          title="Clôturées"
          value={stats.closed.toString()}
          subtext="Dossiers Clôturés"
          trend="up"
          color="secondary"
          icon={<XCircle className="w-5 h-5" />}
        />
      </div>

      <div className="flex flex-col gap-4">
        <ApplicationsFilters
          search={searchQuery}
          onSearchChange={updateSearch}
          statusFilter={statusFilter}
          onStatusChange={(v) => updateFilter('status', v)}
          // spaceFilter non-supporté par l'API actuelle, on le laisse en 'all'
          spaceFilter="all"
          onSpaceChange={() => {}}
          onReset={resetFilters}
          hasActiveFilters={statusFilter !== 'all' || searchQuery.trim() !== ''}
          filteredCount={applications.length}
          totalCount={pagination.total}
        />
        <ApplicationsTable
          applications={applications}
          hasActiveFilters={statusFilter !== 'all' || searchQuery.trim() !== ''}
          onReset={resetFilters}
          onViewApplication={handleViewApplication}
        />
        <Pagination
          totalPages={pagination.totalPages}
          currentPage={pagination.page}
          onPageChange={setPage}
          filteredCount={pagination.total}
        />
      </div>
    </div>
  );
}

interface PaginationProps {
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  filteredCount: number;
}

function Pagination({ totalPages, currentPage, onPageChange, filteredCount }: PaginationProps) {
  if (totalPages <= 1) return null;
  return (
    <div className="bg-slate-50 border-t border-slate-200 px-5 py-3 flex flex-col sm:flex-row items-center justify-between gap-4 rounded-b-xl">
      <span className="text-sm text-slate-500 font-medium">
        Page {currentPage} sur {totalPages} · {filteredCount} résultats
      </span>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          className="p-1.5 rounded border border-slate-200 text-slate-500 hover:bg-white disabled:opacity-40 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
          <button
            key={p}
            onClick={() => onPageChange(p)}
            className={cn(
              'w-8 h-8 flex items-center justify-center rounded border text-sm font-medium transition-colors',
              p === currentPage
                ? 'bg-primary text-white border-primary shadow-sm'
                : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50',
            )}
          >
            {p}
          </button>
        ))}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          className="p-1.5 rounded border border-slate-200 text-slate-500 hover:bg-white disabled:opacity-40 transition-colors"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
